import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { collection, deleteDoc, doc, getDocs, query, where } from 'firebase/firestore';
import { toast } from 'react-toastify';
import {
  LuClipboardList,
  LuEye,
  LuFileSpreadsheet,
  LuHeartPulse,
  LuHouse,
  LuLoaderCircle,
  LuPencil,
  LuPill,
  LuPlus,
  LuSearch,
  LuSearchX,
  LuTrash2,
  LuUserCheck,
  LuUserRoundX,
  LuUsers,
  LuX,
  LuCalendarClock,
} from 'react-icons/lu';
// Only global theme variables are left in this file; importing it keeps their place in the cascade
import './PatientsModal.module.css';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { GLASS_CHIP, GLASS_FIELD } from '../../components/ui/glass';
import { SelectInput } from '../../components/form/FormControls';
import { db } from '../../config/firebaseConfig';
import useMediaQuery from '../../hooks/useMediaQuery';
import { useTranslation } from '../../i18n/LanguageContext';
import AddPatientModal from './AddPatientModal';
import PatientStatusBadge from './patients/PatientStatusBadge';
import { PATIENT_STATUSES, statusOf } from './patients/patientStatuses';

const HEAD = 'tw-sticky tw-top-0 tw-z-[1] tw-min-w-0 tw-whitespace-nowrap tw-border-b tw-border-slate-200 tw-bg-slate-50 tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-[0.08em] tw-text-slate-500 dark:tw-border-white/10 dark:tw-bg-[#262a34] dark:tw-text-slate-400';
const CELL = 'tw-whitespace-nowrap tw-border-b tw-border-slate-100 tw-px-3 tw-py-2.5 tw-align-middle dark:tw-border-white/[0.06]';

const createdAtTime = (patient) => {
  const value = patient.createdAt?.toDate?.() || new Date(patient.createdAt);
  return Number.isNaN(value?.getTime?.()) ? 0 : value.getTime();
};

/** Excel list of a project's patients (Turkish headers and statuses, as before). */
const exportPatientsExcel = async (patients, project) => {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Hasta Listesi');
  worksheet.columns = [
    { header: 'Hasta ID', key: 'patientId', width: 15 },
    { header: 'Durum', key: 'status', width: 15 },
    { header: 'Kayıt Tarihi', key: 'enrollmentDate', width: 15 },
    { header: 'İlk Doz Tarihi', key: 'firstDoseDate', width: 15 },
    { header: 'Mevcut Visit', key: 'currentVisitNumber', width: 12 },
    { header: 'Home Visit', key: 'hasHomeVisit', width: 12 },
    { header: 'Home Visit Tarihleri', key: 'homeVisitDates', width: 30 },
    { header: 'Notlar', key: 'notes', width: 40 },
  ];
  worksheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF3182CE' } };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
  });
  patients.forEach((patient) => {
    worksheet.addRow({
      patientId: patient.patientId,
      status: statusOf(patient.status)?.excelLabel || patient.status,
      enrollmentDate: patient.enrollmentDate || '-',
      firstDoseDate: patient.firstDoseDate || '-',
      currentVisitNumber: patient.currentVisitNumber || '-',
      hasHomeVisit: patient.hasHomeVisit ? 'Evet' : 'Hayır',
      homeVisitDates: patient.homeVisitDates?.join(', ') || '-',
      notes: patient.notes || '-',
    });
  });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${project.titleShort || 'Proje'}_Hasta_Listesi.xlsx`;
  link.click();
  window.URL.revokeObjectURL(url);
};

const DetailRow = ({ label, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-1 tw-rounded-xl tw-px-2.5 tw-py-2 sm:tw-flex-row sm:tw-items-center sm:tw-gap-4">
    <dt className="tw-shrink-0 tw-text-xs tw-font-medium tw-text-slate-400 sm:tw-w-40 dark:tw-text-slate-500">{label}</dt>
    <dd className="tw-min-w-0 tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{children}</dd>
  </div>
);

DetailRow.propTypes = { label: PropTypes.string.isRequired, children: PropTypes.node };

/**
 * "Hastalar" of a project: summary by status, visit distribution of active
 * patients, search and status filter, add / edit / view / delete, Excel list.
 */
const PatientsModal = ({ project, onClose }) => {
  const { t, language, locale } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const wide = useMediaQuery('(min-width: 640px)');
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [formPatient, setFormPatient] = useState(undefined); // undefined: closed, null: new, object: edit
  const [deletingPatient, setDeletingPatient] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [detailPatient, setDetailPatient] = useState(null);
  const [exporting, setExporting] = useState(false);
  const label = (key, vars) => t(`projectManagement.patients.${key}`, vars);

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(query(collection(db, 'Patients'), where('projectId', '==', project.id)));
      const patientsData = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
      // İstemcide yeniden eskiye sıralanır (bileşik index gerekmesin)
      patientsData.sort((a, b) => createdAtTime(b) - createdAtTime(a));
      setPatients(patientsData);
    } catch (error) {
      console.error('Error fetching patients:', error);
      toast.error(tRef.current('projectManagement.patients.toast.loadError'));
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const filteredPatients = useMemo(() => {
    let filtered = patients;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter((patient) => patient.patientId?.toLowerCase().includes(searchLower) || patient.notes?.toLowerCase().includes(searchLower));
    }
    if (statusFilter !== 'all') filtered = filtered.filter((patient) => patient.status === statusFilter);
    return filtered;
  }, [patients, searchTerm, statusFilter]);

  const countOf = (status) => patients.filter((patient) => patient.status === status).length;
  const stats = [
    { key: 'total', label: label('stats.total'), value: patients.length, icon: LuUsers, tone: 'tw-text-slate-900 dark:tw-text-white' },
    { key: 'enrolled', label: t('projectManagement.patientStatus.enrolled'), value: countOf('enrolled'), icon: LuClipboardList, tone: 'tw-text-violet-700 dark:tw-text-violet-300' },
    { key: 'active', label: label('stats.active'), value: countOf('active'), icon: LuUserCheck, tone: 'tw-text-emerald-700 dark:tw-text-emerald-300' },
    { key: 'onMedication', label: t('projectManagement.patientStatus.onMedication'), value: countOf('onMedication'), icon: LuPill, tone: 'tw-text-cyan-700 dark:tw-text-cyan-300' },
    { key: 'planned', label: t('projectManagement.patientStatus.planned'), value: countOf('planned'), icon: LuCalendarClock, tone: 'tw-text-amber-700 dark:tw-text-amber-300' },
    { key: 'screened', label: t('projectManagement.patientStatus.screened'), value: countOf('screened'), icon: LuClipboardList, tone: 'tw-text-blue-700 dark:tw-text-blue-300' },
    { key: 'screenFail', label: t('projectManagement.patientStatus.screenFail'), value: countOf('screenFail'), icon: LuUserRoundX, tone: 'tw-text-rose-700 dark:tw-text-rose-300' },
    { key: 'home', label: label('stats.homeVisit'), value: patients.filter((patient) => patient.hasHomeVisit).length, icon: LuHouse, tone: 'tw-text-teal-700 dark:tw-text-teal-300' },
  ];

  // Aktif ve ilaç alan hastaların bulundukları visit'e göre dağılımı
  const activeByVisit = Object.entries(patients
    .filter((patient) => patient.status === 'active' || patient.status === 'onMedication')
    .reduce((acc, patient) => {
      const visitNum = patient.currentVisitNumber || 1;
      acc[visitNum] = (acc[visitNum] || 0) + 1;
      return acc;
    }, {}))
    .sort(([a], [b]) => Number(a) - Number(b));

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? dateString : date.toLocaleDateString(locale);
  };

  const handleDeletePatient = async () => {
    setDeleteBusy(true);
    try {
      await deleteDoc(doc(db, 'Patients', deletingPatient.id));
      toast.success(label('toast.deleted'));
      setDeletingPatient(null);
      fetchPatients();
    } catch (error) {
      console.error('Error deleting patient:', error);
      toast.error(label('toast.deleteError'));
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPatientsExcel(patients, project);
      toast.success(label('toast.excelSuccess'));
    } catch (error) {
      console.error('Error exporting Excel:', error);
      toast.error(label('toast.excelError'));
    } finally {
      setExporting(false);
    }
  };

  const filtersActive = Boolean(searchTerm) || statusFilter !== 'all';
  // Small row buttons (IconButton is 40px; its size can't be overridden by class order)
  const rowButtonBase = 'tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-500 tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-400';
  const rowButton = `${rowButtonBase} hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 dark:hover:tw-bg-white/10 dark:hover:tw-text-white`;
  const rowActions = (patient) => (
    <div className="tw-flex tw-shrink-0 tw-items-center tw-justify-end tw-gap-0.5">
      <button type="button" onClick={() => setDetailPatient(patient)} aria-label={label('view')} title={label('view')} className={rowButton}>
        <LuEye aria-hidden="true" className="tw-h-4 tw-w-4" />
      </button>
      <button type="button" onClick={() => setFormPatient(patient)} aria-label={label('edit')} title={label('edit')} className={rowButton}>
        <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
      </button>
      <button
        type="button"
        onClick={() => setDeletingPatient(patient)}
        aria-label={label('delete')}
        title={label('delete')}
        className={`${rowButtonBase} hover:tw-bg-rose-500/10 hover:tw-text-rose-600 dark:hover:tw-text-rose-300`}
      >
        <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
      </button>
    </div>
  );

  let list;
  if (loading) {
    list = (
      <div aria-label={label('loading')} className="tw-space-y-2">
        {[0, 1, 2, 3].map((index) => <span key={index} className="tw-block tw-h-12 tw-animate-pulse tw-rounded-xl tw-bg-slate-200/70 dark:tw-bg-white/[0.06]" />)}
      </div>
    );
  } else if (filteredPatients.length === 0) {
    list = (
      <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center">
        <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
          {filtersActive ? <LuSearchX aria-hidden="true" className="tw-h-7 tw-w-7" /> : <LuHeartPulse aria-hidden="true" className="tw-h-7 tw-w-7" />}
        </span>
        <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{filtersActive ? label('noMatch') : label('empty')}</p>
        {!filtersActive && (
          <Button variant="primary" size="sm" onClick={() => setFormPatient(null)}>
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('addFirst')}
          </Button>
        )}
      </div>
    );
  } else if (wide) {
    list = (
      <div className="tw-max-h-[45vh] tw-overflow-auto tw-rounded-2xl tw-border tw-border-slate-200 glass-scrollbar dark:tw-border-white/10">
        <table className="tw-w-full tw-border-separate tw-border-spacing-0 tw-text-sm">
          <thead className="tw-bg-transparent">
            <tr className="tw-bg-transparent">
              <th scope="col" className={HEAD}>{label('patientId')}</th>
              <th scope="col" className={HEAD}>{label('status')}</th>
              <th scope="col" className={HEAD}>{label('enrollmentDate')}</th>
              <th scope="col" className={HEAD}>{label('firstDoseDate')}</th>
              <th scope="col" className={HEAD}>{label('currentVisit')}</th>
              <th scope="col" className={HEAD}>{label('homeVisit')}</th>
              <th scope="col" className={`${HEAD} tw-text-right`}>{label('actions')}</th>
            </tr>
          </thead>
          <tbody className="tw-bg-transparent">
            {filteredPatients.map((patient) => (
              <tr key={patient.id} className="tw-bg-transparent tw-text-slate-700 dark:tw-text-slate-200">
                <td className={CELL}>
                  <span className="tw-rounded-lg tw-bg-slate-900/[0.05] tw-px-2 tw-py-1 tw-font-mono tw-text-xs tw-font-semibold tw-text-slate-800 dark:tw-bg-white/[0.07] dark:tw-text-slate-100">{patient.patientId}</span>
                </td>
                <td className={CELL}><PatientStatusBadge status={patient.status} /></td>
                <td className={`${CELL} tw-tabular-nums`}>{formatDate(patient.enrollmentDate)}</td>
                <td className={`${CELL} tw-tabular-nums`}>{formatDate(patient.firstDoseDate)}</td>
                <td className={CELL}>
                  {patient.currentVisitNumber
                    ? <span className="tw-rounded-full tw-bg-indigo-500/10 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-indigo-700 dark:tw-text-indigo-300">{label('visitN', { n: patient.currentVisitNumber })}</span>
                    : '-'}
                </td>
                <td className={CELL}>
                  {patient.hasHomeVisit
                    ? <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold tw-text-teal-700 dark:tw-text-teal-300"><LuHouse aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />{t('projectManagement.yes')}</span>
                    : <span className="tw-text-slate-400">-</span>}
                </td>
                <td className={`${CELL} tw-text-right`}>{rowActions(patient)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else {
    list = (
      <ul className="tw-space-y-2">
        {filteredPatients.map((patient) => (
          <li key={patient.id} className={`tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-p-3 ${GLASS_CHIP}`}>
            <div className="tw-min-w-0 tw-flex-1 tw-space-y-1.5">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-font-mono tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white">{patient.patientId}</span>
                <PatientStatusBadge status={patient.status} />
              </div>
              <p className="tw-flex tw-flex-wrap tw-gap-x-3 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
                {patient.currentVisitNumber && <span>{label('visitN', { n: patient.currentVisitNumber })}</span>}
                {patient.enrollmentDate && <span>{label('enrollmentShort')}: {formatDate(patient.enrollmentDate)}</span>}
                {patient.hasHomeVisit && <span className="tw-inline-flex tw-items-center tw-gap-1"><LuHouse aria-hidden="true" className="tw-h-3 tw-w-3" />{label('homeVisit')}</span>}
              </p>
            </div>
            {rowActions(patient)}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <>
      <Modal
        isOpen
        onClose={onClose}
        size="xl"
        title={label('title')}
        subtitle={project.titleShort || project.customer}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30" size="xs">
            <LuHeartPulse aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        actions={(
          <>
            <Button size="sm" onClick={handleExport} disabled={patients.length === 0 || exporting}>
              {exporting
                ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin tw-text-emerald-600 dark:tw-text-emerald-400" />
                : <LuFileSpreadsheet aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-emerald-600 dark:tw-text-emerald-400" />}
              {label('export')}
            </Button>
            <Button variant="primary" size="sm" onClick={() => setFormPatient(null)} className="max-sm:tw-flex-1">
              <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('add')}
            </Button>
          </>
        )}
        footer={<Button variant="ghost" onClick={onClose}>{t('common.close')}</Button>}
      >
        <div className="tw-space-y-5">
          {/* Summary */}
          <section aria-label={label('summary')} className="tw-space-y-3">
            <dl className="tw-grid tw-grid-cols-2 tw-gap-2 sm:tw-grid-cols-4">
              {stats.map((stat) => {
                const Icon = stat.icon;
                return (
                  <div key={stat.key} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/60 tw-px-3 tw-py-2.5 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                    <Icon aria-hidden="true" className={`tw-h-5 tw-w-5 tw-shrink-0 ${stat.tone}`} />
                    <div className="tw-min-w-0">
                      <dd className={`tw-text-lg tw-font-semibold tw-leading-none tw-tabular-nums ${stat.tone}`}>
                        {loading ? <span aria-hidden="true" className="tw-block tw-h-5 tw-w-6 tw-animate-pulse tw-rounded tw-bg-slate-200/80 dark:tw-bg-white/10" /> : stat.value.toLocaleString(locale)}
                      </dd>
                      <dt className="tw-mt-1 tw-truncate tw-text-[11px] tw-font-medium tw-text-slate-500 dark:tw-text-slate-400">{stat.label}</dt>
                    </div>
                  </div>
                );
              })}
            </dl>
            {activeByVisit.length > 0 && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                <span className="tw-text-xs tw-font-semibold tw-text-slate-500 dark:tw-text-slate-400">{label('visitDistribution')}</span>
                {activeByVisit.map(([visitNum, count]) => (
                  <span key={visitNum} className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-indigo-500/10 tw-px-2.5 tw-py-1 tw-text-xs tw-text-indigo-800 dark:tw-text-indigo-200">
                    <span className="tw-font-semibold">{label('visitN', { n: visitNum })}</span>
                    <span className="tw-opacity-75">{label('patientCount', { count })}</span>
                  </span>
                ))}
              </div>
            )}
          </section>

          {/* Search and filter */}
          <div className="tw-flex tw-flex-col tw-gap-2 sm:tw-flex-row sm:tw-items-center">
            <div className="tw-relative tw-min-w-0 sm:tw-flex-1">
              <LuSearch aria-hidden="true" className="tw-pointer-events-none tw-absolute tw-left-3.5 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-slate-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder={label('searchPlaceholder')}
                aria-label={label('searchPlaceholder')}
                className={`${GLASS_FIELD} tw-pl-10 tw-pr-10`}
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm('')}
                  aria-label={t('dataTable.search.clear')}
                  className="tw-absolute tw-right-1.5 tw-top-1/2 tw-grid tw-h-7 tw-w-7 -tw-translate-y-1/2 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
                >
                  <LuX aria-hidden="true" className="tw-h-4 tw-w-4" />
                </button>
              )}
            </div>
            <div className="sm:tw-w-56">
              <SelectInput
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
                aria-label={label('statusFilter')}
                options={[
                  { value: 'all', label: label('allStatuses') },
                  ...PATIENT_STATUSES.map((status) => ({ value: status.value, label: t(`projectManagement.patientStatus.${status.value}`) })),
                ]}
              />
            </div>
          </div>

          {list}

          {!loading && (
            <p className="tw-text-right tw-text-xs tw-text-slate-500 tw-tabular-nums dark:tw-text-slate-400">
              {label('showing', { shown: filteredPatients.length, total: patients.length })}
            </p>
          )}
        </div>
      </Modal>

      {/* Detail view */}
      <Modal
        isOpen={Boolean(detailPatient)}
        onClose={() => setDetailPatient(null)}
        size="md"
        title={label('detailTitle')}
        subtitle={detailPatient?.patientId}
        closeLabel={t('common.close')}
        lang={language}
        icon={(
          <GlassIcon gradient="tw-from-rose-400 tw-to-pink-600 tw-shadow-rose-500/30" size="xs">
            <LuEye aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
          </GlassIcon>
        )}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setDetailPatient(null)}>{t('common.close')}</Button>
            <Button
              variant="primary"
              onClick={() => {
                const target = detailPatient;
                setDetailPatient(null);
                setFormPatient(target);
              }}
            >
              <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
              {label('edit')}
            </Button>
          </>
        )}
      >
        {detailPatient && (
          <dl className="tw-divide-y tw-divide-slate-100 dark:tw-divide-white/[0.06]">
            <DetailRow label={label('patientId')}><span className="tw-font-mono">{detailPatient.patientId}</span></DetailRow>
            <DetailRow label={label('status')}><PatientStatusBadge status={detailPatient.status} /></DetailRow>
            <DetailRow label={label('enrollmentDate')}>{formatDate(detailPatient.enrollmentDate)}</DetailRow>
            <DetailRow label={label('firstDoseDate')}>{formatDate(detailPatient.firstDoseDate)}</DetailRow>
            <DetailRow label={label('currentVisit')}>{detailPatient.currentVisitNumber ? label('visitN', { n: detailPatient.currentVisitNumber }) : '-'}</DetailRow>
            <DetailRow label={label('homeVisit')}>{detailPatient.hasHomeVisit ? t('projectManagement.yes') : t('projectManagement.no')}</DetailRow>
            {detailPatient.hasHomeVisit && detailPatient.homeVisitDates?.length > 0 && (
              <DetailRow label={label('homeVisitDates')}>
                <span className="tw-flex tw-flex-wrap tw-gap-1.5">
                  {detailPatient.homeVisitDates.map((date) => (
                    <span key={date} className="tw-rounded-full tw-bg-teal-500/10 tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-teal-800 tw-tabular-nums dark:tw-text-teal-200">{formatDate(date)}</span>
                  ))}
                </span>
              </DetailRow>
            )}
            {detailPatient.homeVisitInfo && <DetailRow label={label('homeVisitInfo')}><span className="tw-whitespace-pre-line tw-break-words">{detailPatient.homeVisitInfo}</span></DetailRow>}
            {detailPatient.notes && <DetailRow label={label('notes')}><span className="tw-whitespace-pre-line tw-break-words">{detailPatient.notes}</span></DetailRow>}
          </dl>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(deletingPatient)}
        title={label('deleteTitle')}
        message={label('deleteMessage', { id: deletingPatient?.patientId || '' })}
        icon={LuTrash2}
        tone="danger"
        confirmLabel={label('deleteConfirm')}
        cancelLabel={t('dataTable.form.cancel')}
        busy={deleteBusy}
        onConfirm={handleDeletePatient}
        onCancel={() => setDeletingPatient(null)}
      />

      {formPatient !== undefined && (
        <AddPatientModal
          project={project}
          patient={formPatient}
          onClose={() => setFormPatient(undefined)}
          onSave={() => {
            setFormPatient(undefined);
            fetchPatients();
          }}
        />
      )}
    </>
  );
};

PatientsModal.propTypes = {
  project: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default PatientsModal;
